import { Icon } from '@clarion-blueprint-shim';
import { captureException } from '@sentry/react';
import { useEffect } from 'react';
import { useRouteError } from 'react-router';
import { contactLink } from '../account/FeedbackButtons';
import { ClarionButton } from '../clarion/ClarionButton';
import { ClarionButtonLink } from '../clarion/ClarionButtonLink';
import { ClarionCard } from '../clarion/ClarionCard';
import { ClarionNonIdealState } from '../clarion/ClarionNonIdealState';
import recapLogo from '../shared/recapLogo.svg';
import supportLinks from '../shared/supportLinks';
import { Component as NotFound } from './NotFoundPage';

export function RouterErrorBoundary() {
  const error = useRouteError() as Error;
  useEffect(() => {
    console.error(error);
    captureException(error);
  }, [error]);

  return <ErrorView debugInfo={`${JSON.stringify((error as any)?.message || error)}`} />;
}

export function ErrorView({ debugInfo }: { debugInfo?: string }) {
  if (debugInfo?.toLowerCase().includes('not found')) {
    return <NotFound />;
  }
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <ClarionCard className="max-w-md w-full">
        <div className="flex flex-col items-center mb-6 text-balance">
          <ClarionNonIdealState
            icon={<Icon icon="error" intent="danger" size={48} />}
            title="Sorry, something went wrong."
            description="This may be temporary—try reloading the page, or contact us if the issue persists."
          />
        </div>
        <div className="flex flex-col gap-3">
          <ClarionButton onClick={() => window.location.reload()} intent="primary">
            Reload Page
          </ClarionButton>
          <ClarionButtonLink
            icon="send-message"
            children="Report a Problem"
            target="_blank"
            href={contactLink(
              'Recap Error Report',
              'Please retain the information below. You may include any additional information, such as actions taken before the error happened.',
              `${window.location.pathname}  ${debugInfo}`,
            )}
          />
        </div>
        <details className="mt-4">
          <summary className={`cursor-pointer opacity-80`}>Error Details</summary>
          <ClarionCard className="mt-2 p-0!" compact>
            <pre className="p-3 whitespace-pre-wrap overflow-auto">{debugInfo}</pre>
          </ClarionCard>
        </details>
      </ClarionCard>
      <a
        href={supportLinks.about}
        target="_blank"
        className="flex items-center gap-2 mt-6"
        rel="noreferrer"
      >
        <img src={recapLogo} alt="Recap" className="h-6" />
        <span className={`text-sm`}>Recap Innovations</span>
      </a>
    </div>
  );
}
